import ApiConfig from 'src/config/api-config';
import { auth } from 'src/config/firebase';

export interface ApiEnvelope<T> {
  ok: boolean;
  request_id: string;
  data: T;
  meta?: unknown;
}

export interface ApiErrorDetail {
  code: string;
  sub_code?: string;
  error_title?: string;
  message: string;
  user_message: string;
  field_errors?: Record<string, string>;
  retryable?: boolean;
  next_action?: string;
}

export interface ApiErrorEnvelope {
  ok: false;
  request_id: string;
  error: ApiErrorDetail;
}

export class SubscriptionApiError extends Error {
  code: string;
  subCode?: string;
  errorTitle?: string;
  userMessage: string;
  fieldErrors?: Record<string, string>;
  retryable?: boolean;
  nextAction?: string;
  status: number;

  constructor(status: number, err: ApiErrorDetail) {
    super(err.message || 'Subscription API error');
    this.name = 'SubscriptionApiError';
    this.code = err.code || 'ERR_INTERNAL';
    this.subCode = err.sub_code;
    this.errorTitle = err.error_title;
    this.userMessage = err.user_message || err.message || 'An unexpected error occurred.';
    this.fieldErrors = err.field_errors;
    this.retryable = err.retryable ?? false;
    this.nextAction = err.next_action;
    this.status = status;
  }
}

// Helper to get token
const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user)
    throw new SubscriptionApiError(401, {
      code: 'ERR_UNAUTHENTICATED',
      message: 'User is not logged in',
      user_message: 'Please log in to continue.',
    });
  return await user.getIdToken();
};

// Generic request wrapper
async function requestApi<T>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    params?: Record<string, string | number | boolean | undefined>;
    idempotencyKey?: string;
  } = {},
): Promise<T> {
  const token = await getAuthToken();
  const method = options.method || 'GET';

  // Target unified subscription_api dispatcher with direct endpoint fallback
  let url = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}subscription_api/${endpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        searchParams.append(k, String(v));
      }
    });
    const qs = searchParams.toString();
    if (qs) {
      url += (url.includes('?') ? '&' : '?') + qs;
    }
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Client-Version': '1.0.0',
  };

  if (['POST', 'PUT', 'DELETE'].includes(method)) {
    headers['Idempotency-Key'] =
      options.idempotencyKey || `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  if (options.body && ['POST', 'PUT', 'DELETE'].includes(method)) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(url, fetchOptions);
    if (response.status === 404) {
      const directUrl =
        `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${endpoint}` +
        (url.includes('?') ? `?${url.split('?')[1]}` : '');
      const directResp = await fetch(directUrl, fetchOptions);
      if (directResp.status !== 404) {
        response = directResp;
      }
    }
  } catch (netErr: any) {
    throw new SubscriptionApiError(503, {
      code: 'ERR_NETWORK',
      message: netErr?.message || 'Network request failed',
      user_message: 'Unable to connect to server. Please check your internet connection.',
      retryable: true,
    });
  }

  let jsonResult: any;
  try {
    jsonResult = await response.json();
  } catch {
    jsonResult = null;
  }

  if (!response.ok || (jsonResult && jsonResult.ok === false)) {
    const errDetail: ApiErrorDetail = jsonResult?.error || {
      code: response.status === 401 ? 'ERR_UNAUTHENTICATED' : 'ERR_INTERNAL',
      message: response.statusText || 'Request failed',
      user_message: 'An error occurred while communicating with the billing service.',
      retryable: response.status >= 500,
    };
    throw new SubscriptionApiError(response.status, errDetail);
  }

  if (jsonResult && jsonResult.ok === true && jsonResult.data !== undefined) {
    return jsonResult.data as T;
  }

  return (jsonResult?.data ?? jsonResult) as T;
}

// ==========================================
// TYPES
// ==========================================

export interface EntitlementsData {
  tier: 'free_basic' | 'pro';
  subscription_status: string;
  is_pro: boolean;
  access_until: string | null;
  features: {
    ai_program_generator: boolean;
    ai_exercise_replacement: boolean;
    ai_shuffle: boolean;
    motion_tracking: boolean;
    advanced_analytics: boolean;
    unlimited_clients: boolean;
    max_active_clients: number | null;
    export_invoices: boolean;
    contact_support: boolean;
  };
}

export interface PlanItem {
  tier_slug: string;
  name: string;
  description: string;
  interval: 'month' | 'year';
  stripe_product_id?: string | null;
  stripe_price_id?: string | null;
  price_id: string | null;
  currency: string;
  currency_code?: string;
  currency_symbol?: string;
  amount_minor: number;
  formatted_amount: string;
  display_price?: string;
  billing_period: string;
  list_price?: string | null;
  sale_price?: string | null;
  catalog_discount?: number;
  catalog_discount_amount?: number;
  catalog_discount_percentage?: number;
  discount_amount?: number;
  discount_percentage?: number;
  yearly_savings_percentage?: number;
  savings_percentage?: number;
  savings_percent: number;
  applied_promotion_code?: string | null;
  promotion_type?: 'percent' | 'amount' | null;
  promotion_discount_amount?: number;
  promotion_discount_percentage?: number;
  badges: string[];
  recommended: boolean;
  tagline?: string;
  features: string[];
  trial_eligible?: boolean;
  trial_days?: number;
  trial_information?: {
    eligible: boolean;
    days: number;
    text: string;
  };
  is_current: boolean;
}

export interface SubscriptionPlansData {
  country?: string;
  currency: string;
  currency_symbol?: string;
  trial_days: number;
  trial_eligible: boolean;
  screen_variant: 'with_trial' | 'without_trial';
  current_status: string;
  plans: PlanItem[];
  catalog_version: number;
}

export interface TrialEligibilityData {
  trial_eligible: boolean;
  trial_days: number;
  reason: string;
}

export interface StripeConfigData {
  publishable_key: string;
}

export interface PromoCodeData {
  valid: boolean;
  code: string;
  promotion_code_id: string | null;
  coupon_id: string;
  discount_percent: number;
  percent_off?: number;
  amount_off: number;
  duration: string;
  name: string;
  message?: string;
}

export interface CheckoutPreviewPayload {
  tier_slug?: string;
  interval: 'month' | 'year';
  price_id?: string | null;
  promo_code?: string;
  country?: string;
  billing_profile?: Partial<BillingProfileData>;
  trial_selection?: boolean;
  trial_choice?: boolean;
}

export interface CheckoutPreviewData {
  quote_id?: string;
  country?: string;
  price_id: string;
  stripe_price_id?: string;
  interval: 'month' | 'year';
  tier_slug?: string;
  currency: string;
  currency_symbol?: string;
  base_amount_minor: number;
  subtotal_minor?: number;
  formatted_base_amount: string;
  formatted_subtotal?: string;
  // Catalog / Yearly Savings
  catalog_discount?: number;
  catalog_discount_amount?: number;
  formatted_catalog_discount?: string | null;
  yearly_savings_percentage?: number;
  savings_percentage?: number;
  savings_percent?: number;
  list_price?: string;
  // Promo-Code Specific Fields
  applied_promotion_code?: string | null;
  applied_promo_code?: string | null;
  promotion_type?: 'percent' | 'amount' | null;
  promotion_amount?: number;
  promotion_discount_amount?: number;
  promotion_discount_amount_minor?: number;
  formatted_promotion_amount?: string | null;
  formatted_promotion_discount_amount?: string | null;
  promotion_percentage?: number;
  promotion_discount_percentage?: number;
  // Unified Discount Aliases
  discount_minor: number;
  formatted_discount: string | null;
  discount_percent?: number;
  discount_percentage?: number;
  percent_off?: number;
  // Net Subtotal
  net_amount_minor: number;
  discounted_subtotal_minor?: number;
  formatted_net_amount: string;
  formatted_discounted_subtotal?: string;
  tax_minor: number;
  formatted_tax: string;
  tax_rate_percent: number;
  tax_status?: string;
  is_tax_inclusive?: boolean;
  tax_display_name?: string;
  net_before_tax_minor?: number;
  formatted_net_before_tax?: string;
  total_minor: number;
  formatted_total: string;
  due_today_minor: number;
  formatted_due_today: string;
  first_payment_after_trial_minor?: number;
  formatted_first_payment_after_trial?: string;
  trial_eligible: boolean;
  is_trial?: boolean;
  trial_days: number;
  trial_ends_at?: string | null;
  formatted_trial_ends_at?: string | null;
  valid?: boolean;
  code?: string;
  message?: string;
  applied_promo: {
    code: string;
    promotion_code_id?: string | null;
    coupon_id?: string;
    discount_type?: 'percent' | 'amount';
    promotion_type?: 'percent' | 'amount';
    discount_percent: number;
    promotion_discount_percentage?: number;
    discount_minor: number;
    promotion_discount_amount?: number;
    formatted_discount?: string;
    formatted_promotion_discount_amount?: string;
    name?: string;
  } | null;
}

export interface CheckoutSubscriptionPayload {
  tier_slug?: string;
  interval: 'month' | 'year';
  price_id?: string | null;
  payment_method_id?: string;
  promo_code?: string;
  billing_profile_version?: string;
  trial_choice?: boolean;
  trial_selection?: boolean;
  quote_id?: string;
  country?: string;
}

export interface CheckoutSubscriptionData {
  subscription_id: string;
  status: 'trialing' | 'active' | 'processing' | 'past_due';
  display_status: string;
  currency?: string;
  amount_minor?: number;
  unit_amount_minor?: number;
  formatted_amount?: string;
  subtotal_minor?: number;
  discount_minor?: number;
  tax_minor?: number;
  total_minor?: number;
  amount_paid_minor?: number;
  due_today_minor?: number;
  formatted_subtotal?: string;
  formatted_discount?: string;
  formatted_tax?: string;
  formatted_total?: string;
  formatted_amount_paid?: string;
  formatted_due_today?: string;
  trial_ends_at?: string | null;
  next_action: string;
  is_trial: boolean;
  message?: string;
  operation_id?: string;
}

export interface BillingProfileData {
  billingType: 'individual' | 'business';
  fullName: string;
  companyName: string;
  billingEmail: string;
  country: string;
  stateRegion: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  companyRegistrationNumber: string;
  taxVatNumber: string;
  tax_status?: string;
  tax_rate_percent?: number;
  tax_id_verification_status?: string | null;
  isDefault?: boolean;
  updated_at?: string;
}

export interface ValidateBillingProfileResponse {
  valid: boolean;
  tax_rate: number;
  tax_rate_percent: number;
  tax_status: string;
}

export interface PaymentCardData {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  expiry: string;
  cardholder_name: string;
  is_default: boolean;
  isDefault: boolean;
}

export interface PaymentMethodsData {
  payment_methods: PaymentCardData[];
  default_payment_method_id: string | null;
}

export interface SubscriptionStateData {
  status: 'active' | 'trialing' | 'cancelled' | 'past_due' | 'inactive';
  display_status: string;
  tier_slug: string;
  billing_interval: 'month' | 'year';
  amount: {
    currency: string;
    amount_minor: number;
    formatted: string;
  };
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  access_until: string | null;
  trial: {
    active: boolean;
    ends_at: string | null;
    first_charge_at: string | null;
  };
  past_due: {
    amount_due: string | null;
    grace_period_ends_at: string | null;
    retry_count: number;
  };
  default_payment_method: {
    id: string;
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    cardholder_name: string;
  } | null;
  pending_update: {
    pending_interval: 'month' | 'year';
    pending_price_id: string;
    pending_change_at: string;
    stripe_schedule_id?: string;
    formatted_target_price: string;
    message: string;
  } | null;
  has_used_trial: boolean;
  has_had_subscription?: boolean;
}

export interface BillingHistoryItem {
  id: string;
  invoice_number: string;
  date: string;
  formatted_date: string;
  description: string;
  plan_name: string;
  billing_interval: string;
  period: string;
  amount_minor: number;
  currency: string;
  formatted_amount: string;
  amount_refunded_minor: number;
  formatted_amount_refunded: string | null;
  status: 'PAID' | 'PENDING' | 'PROCESSING' | 'FAILED' | 'REFUNDED' | 'CANCELLED';
  status_label: string;
  badge_variant: string;
  refund_status: string;
  pdf_available: boolean;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
}

export interface BillingHistoryData {
  current_page: number;
  page: number;
  total_pages: number;
  total_count: number;
  limit: number;
  invoices: BillingHistoryItem[];
  items?: BillingHistoryItem[];
  pagination?: {
    current_page: number;
    total_pages: number;
    total_items: number;
    limit: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface SupportScheduleItem {
  days: string;
  hours: string;
  is_closed: boolean;
}

export interface SupportConfigData {
  topics: Array<{ code: string; label: string }>;
  support_email: string;
  support_uid?: string;
  support_name?: string;
  availability: {
    timezone: string;
    description?: string;
    schedule?: SupportScheduleItem[];
    weekdays: Record<
      string,
      { enabled: boolean; opens_at: string | null; closes_at: string | null }
    >;
    is_open_now: boolean;
    current_day: string;
    current_time_utc: string;
    next_change_at: string | null;
  };
  tips?: {
    title?: string;
    subtitle?: string;
    items?: string[];
  };
  contact_methods?: {
    email?: {
      title?: string;
      subtitle?: string;
      email_address?: string;
      reply_time_note?: string;
    };
  };
  config_version: number;
  expected_response_hours: number;
  max_message_chars: number;
  max_attachments: number;
  max_file_size_mb: number;
  allowed_mime_types: string[];
  allowed_extensions: string[];
}

export interface SupportTicketPayload {
  topic: string;
  subject: string;
  message: string;
  attachments?: Array<{
    file_name: string;
    storage_path: string;
    file_size_bytes?: number;
    content_type?: string;
  }>;
}

export interface SupportTicketResponse {
  ticket_id: string;
  chat_id?: string;
  support_uid?: string;
  status: string;
  reply_email: string;
  expected_response_time: string;
  message: string;
  next_action?: string;
}

// ==========================================
// EXPORTED SERVICE API
// ==========================================

export const subscriptionService = {
  // 1. Entitlements
  getEntitlements: () => requestApi<EntitlementsData>(ApiConfig.TRAINER_GET_ENTITLEMENTS),

  checkFeatureAccess: (feature: string, source?: string) =>
    requestApi<{ allowed: boolean; feature: string; tier: string; message: string }>(
      ApiConfig.TRAINER_CHECK_FEATURE_ACCESS,
      { method: 'POST', body: { feature, source } },
    ),

  // 2. Plans & Eligibility
  getSubscriptionPlans: (country?: string) =>
    requestApi<SubscriptionPlansData>(ApiConfig.TRAINER_GET_SUBSCRIPTION_PLANS, {
      params: country ? { country } : undefined,
    }),

  getTrialEligibility: () =>
    requestApi<TrialEligibilityData>(ApiConfig.TRAINER_GET_TRIAL_ELIGIBILITY),

  previewCheckout: (payload: CheckoutPreviewPayload) =>
    requestApi<CheckoutPreviewData>(ApiConfig.TRAINER_PREVIEW_CHECKOUT, {
      method: 'POST',
      body: payload,
    }),

  validatePromoCode: (payload: {
    code: string;
    tier_slug?: string;
    interval?: 'month' | 'year';
    price_id?: string | null;
    country?: string;
    billing_profile?: Partial<BillingProfileData>;
  }) =>
    requestApi<CheckoutPreviewData>(ApiConfig.TRAINER_VALIDATE_PROMO_CODE, {
      method: 'POST',
      body: payload,
    }),

  // 3. Checkout & SCA
  checkoutSubscription: (payload: CheckoutSubscriptionPayload) =>
    requestApi<CheckoutSubscriptionData>(ApiConfig.TRAINER_CHECKOUT_SUBSCRIPTION, {
      method: 'POST',
      body: payload,
    }),

  syncSubscription: () =>
    requestApi<{
      subscription_id: string;
      status: string;
      display_status: string;
      is_active: boolean;
      next_action: string;
    }>(ApiConfig.TRAINER_SYNC_SUBSCRIPTION, { method: 'POST' }),

  getPaymentOperationStatus: (operationId: string) =>
    requestApi<{ operation_id: string; status: string; subscription_id?: string }>(
      ApiConfig.TRAINER_GET_PAYMENT_OPERATION_STATUS,
      { params: { operation_id: operationId } },
    ),

  // 4. Legal
  getTermsOfService: () =>
    requestApi<{
      title: string;
      version: string;
      content: string;
      summary: string;
      effective_date?: string;
    }>(ApiConfig.TRAINER_GET_TERMS_OF_SERVICE),

  getPrivacyPolicy: () =>
    requestApi<{
      title: string;
      version: string;
      content: string;
      summary: string;
      effective_date?: string;
    }>(ApiConfig.TRAINER_GET_PRIVACY_POLICY),

  recordLegalAcknowledgement: (termsVersion?: string, privacyVersion?: string) =>
    requestApi<{ acknowledgement_id: string; accepted: boolean; timestamp: string }>(
      ApiConfig.TRAINER_RECORD_LEGAL_ACKNOWLEDGEMENT,
      {
        method: 'POST',
        body: {
          ...(termsVersion ? { terms_version: termsVersion } : {}),
          ...(privacyVersion ? { privacy_version: privacyVersion } : {}),
          accepted: true,
        },
      },
    ),

  // 5. Billing Profile
  getBillingProfile: () => requestApi<BillingProfileData>(ApiConfig.TRAINER_GET_BILLING_PROFILE),

  saveBillingProfile: (profile: Partial<BillingProfileData>) =>
    requestApi<BillingProfileData>(ApiConfig.TRAINER_SAVE_BILLING_PROFILE, {
      method: 'PUT',
      body: profile,
    }),

  validateBillingProfile: (profile: Partial<BillingProfileData>) =>
    requestApi<ValidateBillingProfileResponse>(ApiConfig.TRAINER_VALIDATE_BILLING_PROFILE, {
      method: 'POST',
      body: profile,
    }),

  // 6. Payment Methods
  createSetupIntent: () =>
    requestApi<{ client_secret: string; setup_intent_id: string; customer_id: string }>(
      ApiConfig.TRAINER_CREATE_SETUP_INTENT,
      { method: 'POST' },
    ),

  getStripeConfig: () => requestApi<StripeConfigData>(ApiConfig.TRAINER_GET_STRIPE_CONFIG),

  getPaymentMethods: () => requestApi<PaymentMethodsData>(ApiConfig.TRAINER_GET_PAYMENT_METHODS),

  savePaymentMethod: (payload: {
    payment_method_id?: string;
    cardholder_name?: string;
    is_default?: boolean;
  }) =>
    requestApi<PaymentCardData>(ApiConfig.TRAINER_SAVE_PAYMENT_METHOD, {
      method: 'POST',
      body: payload,
    }),

  setDefaultPaymentMethod: (pmId: string) =>
    requestApi<{ success: boolean; default_payment_method_id: string; message: string }>(
      ApiConfig.TRAINER_SET_DEFAULT_PAYMENT_METHOD,
      { method: 'POST', body: { payment_method_id: pmId } },
    ),

  replacePaymentMethod: (payload: { payment_method_id?: string; cardholder_name?: string }) =>
    requestApi<PaymentCardData>(ApiConfig.TRAINER_REPLACE_PAYMENT_METHOD, {
      method: 'POST',
      body: payload,
    }),

  deletePaymentMethod: (pmId: string) =>
    requestApi<{ success: boolean; deleted_payment_method_id: string; message: string }>(
      ApiConfig.TRAINER_DELETE_PAYMENT_METHOD,
      { method: 'DELETE', body: { payment_method_id: pmId } },
    ),

  // 7. My Subscription
  getSubscriptionState: () =>
    requestApi<SubscriptionStateData>(ApiConfig.TRAINER_GET_SUBSCRIPTION_STATE),

  cancelSubscription: (reason: string, feedback?: string) =>
    requestApi<{
      status: string;
      cancel_at_period_end: boolean;
      access_until: string;
      message: string;
    }>(ApiConfig.TRAINER_CANCEL_SUBSCRIPTION, { method: 'POST', body: { reason, feedback } }),

  reactivateSubscription: () =>
    requestApi<{
      status: string;
      display_status: string;
      cancel_at_period_end: boolean;
      message: string;
    }>(ApiConfig.TRAINER_REACTIVATE_SUBSCRIPTION, { method: 'POST' }),

  previewIntervalSwitch: (targetInterval: 'month' | 'year') =>
    requestApi<{
      current_interval: string;
      target_interval: string;
      switch_timing: string;
      prorated_charge_minor: number;
      formatted_prorated_charge: string;
      regular_renewal_price: string;
      effective_date: string;
      message: string;
    }>(ApiConfig.TRAINER_SWITCH_BILLING_INTERVAL_PREVIEW, {
      method: 'POST',
      body: { target_interval: targetInterval },
    }),

  switchInterval: (targetInterval: 'month' | 'year') =>
    requestApi<{
      success: boolean;
      billing_interval: string;
      pending_interval?: string;
      effective_date?: string;
      message: string;
    }>(ApiConfig.TRAINER_SWITCH_BILLING_INTERVAL, {
      method: 'POST',
      body: { target_interval: targetInterval },
    }),

  cancelScheduledIntervalSwitch: () =>
    requestApi<{ success: boolean; message: string }>(
      ApiConfig.TRAINER_CANCEL_SCHEDULED_INTERVAL_SWITCH,
      { method: 'POST' },
    ),

  retryPastDuePayment: () =>
    requestApi<{ invoice_id: string; status: string; message: string }>(
      ApiConfig.TRAINER_RETRY_INVOICE_PAYMENT,
      { method: 'POST' },
    ),

  // 8. Billing History & Invoices
  getBillingHistory: (
    params: { page?: number; limit?: number; status?: string; search?: string; sort?: string } = {},
  ) =>
    requestApi<BillingHistoryData>(ApiConfig.TRAINER_GET_BILLING_HISTORY, {
      params,
    }),

  getInvoiceDownloadUrl: (invoiceId: string) =>
    requestApi<{
      invoice_id: string;
      download_url: string;
      invoice_pdf?: string;
      hosted_invoice_url?: string;
    }>(ApiConfig.TRAINER_GET_INVOICE_DOWNLOAD_URL, { params: { invoice_id: invoiceId } }),

  downloadAllInvoices: () =>
    requestApi<{
      export_id: string;
      status: string;
      download_url: string;
      message: string;
      cached?: boolean;
      total_invoices?: number;
    }>(ApiConfig.TRAINER_DOWNLOAD_ALL_INVOICES, { method: 'POST' }),

  getExportStatus: (exportId: string) =>
    requestApi<{ export_id: string; status: string; download_url?: string }>(
      ApiConfig.TRAINER_GET_EXPORT_STATUS,
      { params: { export_id: exportId } },
    ),

  // 9. Contact Support
  getSupportConfig: () => requestApi<SupportConfigData>(ApiConfig.TRAINER_GET_SUPPORT_CONFIG),

  contactSupport: (payload: SupportTicketPayload) =>
    requestApi<SupportTicketResponse>(ApiConfig.TRAINER_CONTACT_SUPPORT, {
      method: 'POST',
      body: payload,
    }),

  deleteSupportAttachment: (storagePath: string) =>
    requestApi<{ deleted: boolean; storage_path: string }>(
      ApiConfig.TRAINER_DELETE_SUPPORT_ATTACHMENT,
      {
        method: 'POST',
        body: { storage_path: storagePath },
      },
    ),

  // 10. Account Deletion
  previewAccountDeletion: () =>
    requestApi<{
      has_active_subscription: boolean;
      subscription_action: string;
      payment_methods_action: string;
      data_scope: string;
      invoice_retention: string;
      can_proceed: boolean;
    }>(ApiConfig.TRAINER_PREVIEW_ACCOUNT_DELETION, { method: 'POST' }),

  requestAccountDeletion: (confirmation: string) =>
    requestApi<{ job_id: string; status: string; message: string }>(
      ApiConfig.TRAINER_REQUEST_ACCOUNT_DELETION,
      { method: 'POST', body: { confirmation } },
    ),

  getAccountDeletionStatus: (jobId: string) =>
    requestApi<{ job_id: string; status: string }>(ApiConfig.TRAINER_GET_ACCOUNT_DELETION_STATUS, {
      params: { job_id: jobId },
    }),
};
